import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { wfetch } from "../../../utils/fetch";
import { getApiEndpoint } from "../../../utils/constants/api";
import { decodeHtml } from "../../../utils/richText";
import { getMagnoliaContext } from "../../../magnolia.service";
import { activateScripts } from "../rawHtml/RawHtml";
import { environment } from "../../../environment";
import { getChildNodes } from "../../../utils/nodes";

// Injects the Magnolia Marketing Tags into the document, the headless counterpart of the
// module's scripts area in the FreeMarker templates. Tags are fetched once per site and
// matched like the module does: through their page mapping (pageLink path, optionally with
// subpages) or through the page template of the site's root page. A tag without either
// applies nowhere, so the module's sample tags (Clicky, Eloqua ...) stay out. Inside the page
// editor only tags flagged "active on author" run, so third-party scripts stay out of the
// editing view.
//
// The endpoint only exists with the Marketing Tags module installed
// (demo-ecommerce-base:restEndpoints/delivery/marketing-tags); without it nothing happens.

const PLACEMENTS = {
    headerScripts: (el) => document.head.appendChild(el),
    bodyBeginScripts: (el) => document.body.prepend(el),
    bodyEndScripts: (el) => document.body.appendChild(el),
};

const isTrue = (value) => value === true || value === "true";

// Tag content is written for pages served by Magnolia, so root-relative URLs (a script under
// /.resources/..., an image under /dam/...) mean the Magnolia host, not the storefront's.
const absolutizeUrls = (root) => {
    root.querySelectorAll("[src^='/'], [href^='/']").forEach((el) => {
        ["src", "href"].forEach((attr) => {
            const value = el.getAttribute(attr);
            if (value && value.startsWith("/") && !value.startsWith("//")) el.setAttribute(attr, environment.MAGNOLIA_BASE_URL + value);
        });
    });
};

const appliesTo = (tag, appBase, rootTemplate) => {
    if (tag.pageTemplate && rootTemplate && tag.pageTemplate === rootTemplate) return true;

    const mappings = getChildNodes(tag.pageLink).filter((mapping) => mapping.path);

    // Same rule as the module: the mapped page itself, or its subtree when "insert in
    // subpages" is set. A mapping to the root only covers everything with that flag.
    return mappings.some(({ path, insertInSubPages }) => {
        if (path === appBase) return true;
        return isTrue(insertInSubPages) && appBase.startsWith(path.endsWith("/") ? path : `${path}/`);
    });
};

const MarketingTags = ({ appBase }) => {
    const location = useLocation();
    const rootTemplate = useSelector((state) => state.rootPageData && state.rootPageData["mgnl:template"]);
    const inEditor = !!getMagnoliaContext(location)?.isMagnolia;

    useEffect(() => {
        // Wait for the site's root page: template matching needs it, and injecting before it
        // arrives would run every tag's scripts twice.
        if (!rootTemplate) return undefined;

        let cancelled = false;
        const injected = [];

        // Ask for everything: the delivery default of 10 per page would drop tags silently.
        wfetch(getApiEndpoint("MARKETING_TAGS", { queryParams: { limit: 200 } })).then((response) => {
            if (cancelled || !response || !response.data) return;

            const tags = (response.data.results || []).filter((tag) => tag.content && appliesTo(tag, appBase, rootTemplate));

            tags.forEach((tag) => {
                if (inEditor && !isTrue(tag.activeAuthor)) return;

                const place = PLACEMENTS[tag.placeToPut] || PLACEMENTS.headerScripts;
                const host = document.createElement(tag.placeToPut === "headerScripts" ? "template" : "div");

                host.innerHTML = decodeHtml(tag.content);
                absolutizeUrls(tag.placeToPut === "headerScripts" ? host.content : host);

                // <head> takes no wrapper element: move the parsed nodes over one by one.
                const nodes = tag.placeToPut === "headerScripts" ? [...host.content.childNodes] : [host];
                nodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) node.setAttribute("data-marketing-tag", tag["@name"]);
                    place(node);
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.tagName === "SCRIPT") node.replaceWith(recreateScript(node));
                        else activateScripts(node);
                    }
                    injected.push(node);
                });
            });
        });

        return () => {
            cancelled = true;
            injected.forEach((node) => node.parentNode && node.parentNode.removeChild(node));
            document.querySelectorAll("[data-marketing-tag]").forEach((node) => node.remove());
        };
    }, [appBase, inEditor, rootTemplate]);

    return null;
};

// A <script> moved into <head> from a template is still inert; a fresh element runs.
const recreateScript = (inert) => {
    const script = document.createElement("script");
    [...inert.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
    script.textContent = inert.textContent;
    return script;
};

export default MarketingTags;